import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../apiConfig';
import styles from './ShipArtworkPage.module.css';
import { toast } from 'react-hot-toast';

const packagingOptions = ['Tube', 'Box', 'Crate'];

// Mock data for now
const mockShipments = [
  {
    id: 1,
    thumbnail: 'https://placehold.co/60x60',
    title: 'Sunset Over Water',
    buyer: 'Jane Doe',
    destination: '123 Main St, New York, NY',
    dimensions: '24 x 36 x 2 in',
    weight: '5 lbs',
    packagingType: 'Box',
    status: 'Ready to Ship',
  },
  {
    id: 2,
    thumbnail: 'https://placehold.co/60x60',
    title: 'Abstract Dreams',
    buyer: 'John Smith',
    destination: '456 Elm St, Los Angeles, CA',
    dimensions: '30 x 40 x 3 in',
    weight: '7 lbs',
    packagingType: 'Crate',
    status: 'Ready to Ship',
  },
];

// Helper function to recommend packaging
function recommendPackaging(dimensions, weight, fragility) {
  const dims = dimensions.split('x').map(s => parseFloat(s));
  const w = parseFloat(weight);
  if (dims[2] && dims[2] > 6) return 'Crate';
  if (fragility === 3) return 'Crate';
  if (dims[0] > 40 || dims[1] > 40) return 'Crate';
  if (dims[0] > 30 || dims[1] > 30) return 'Box';
  if (w > 10) return 'Box';
  if (fragility === 1) return 'Tube';
  return 'Box';
}

function checkDimensionalSurcharge(dimensions, packagingType) {
  const dims = dimensions.split('x').map(s => parseFloat(s));
  if (packagingType === 'Box' && (dims[0] > 36 || dims[1] > 36 || dims[2] > 36)) {
    return true;
  }
  return false;
}

const ShipArtworkPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const shipment = mockShipments.find(s => s.id === Number(id));

  const [dimensions, setDimensions] = useState(shipment?.dimensions || '');
  const [weight, setWeight] = useState(shipment?.weight || '');
  const [packagingType, setPackagingType] = useState(shipment?.packagingType || 'Box');
  const [fragility, setFragility] = useState(2);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState('');

  // Shipping rates state
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState(null);

  // Packaging recommendation
  const recommendedPackaging = recommendPackaging(dimensions, weight, fragility);
  const surchargeWarning = checkDimensionalSurcharge(dimensions, packagingType);

  // Fetch rates when relevant fields change
  useEffect(() => {
    if (!dimensions || !weight || !packagingType || !shipment.destination || !shipment.title) return;
    setRatesLoading(true);
    setRatesError(null);
    setSelectedRateId(null);
    setCheckoutUrl('');
    api.post('/shipping/rates', {
      title: shipment.title,
      dimensions,
      weight,
      packagingType,
      destination: shipment.destination
    })
      .then(res => {
        // Map Shopify shipping_lines to our expected rate format
        const { rates, draftOrder } = res.data;
        setCheckoutUrl(draftOrder?.checkout_url || '');
        if (rates && rates.length > 0) {
          setRates(rates.map((rate, idx) => ({
            id: rate.id || `rate_${idx}`,
            carrier: rate.carrier_identifier || rate.title || 'Carrier',
            service: rate.title || 'Service',
            price: rate.price ? parseFloat(rate.price) : 0,
            retail: rate.price ? parseFloat(rate.price) * 1.5 : 0, // Mock retail for now
            est_days: rate.delivery_days || 3
          })));
        } else {
          setRates([]);
        }
        setRatesLoading(false);
      })
      .catch(err => {
        setRatesError('Failed to fetch shipping rates.');
        toast.error('Failed to fetch shipping rates.');
        setRatesLoading(false);
      });
  }, [dimensions, weight, packagingType, shipment.destination, shipment.title]);

  const handleContinue = () => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    }
  };

  if (!shipment) {
    return <div className={styles.container}>Shipment not found.</div>;
  }

  return (
    <div className={styles.container}>
      <button onClick={() => navigate('/shipping')} className={styles.backButton}>&larr; Back to Shipping</button>
      <h2 className={styles.header}>Ship Artwork</h2>
      <div className={styles.artworkInfo}>
        <img src={shipment.thumbnail} alt={shipment.title} className={styles.thumbnail} />
        <div>
          <div className={styles.label}>{shipment.title}</div>
          <div style={{ color: 'var(--text-secondary)' }}>{shipment.buyer}</div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>{shipment.destination}</div>
        </div>
      </div>
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <label className={styles.label}>Dimensions:</label>
        <input value={dimensions} onChange={e => setDimensions(e.target.value)} className={styles.input} />
      </div>
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <label className={styles.label}>Weight:</label>
        <input value={weight} onChange={e => setWeight(e.target.value)} className={styles.input} />
      </div>
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <label className={styles.label}>Packaging Type:</label>
        <select value={packagingType} onChange={e => setPackagingType(e.target.value)} className={styles.select}>
          {packagingOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <label className={styles.label}>Fragility:</label>
        <input type="range" min={1} max={3} value={fragility} onChange={e => setFragility(Number(e.target.value))} className={styles.range} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-xs)' }}>
          <span>Not fragile</span>
          <span>Very fragile</span>
        </div>
      </div>
      <div style={{ marginBottom: 'var(--spacing-lg)', marginTop: 'var(--spacing-lg)' }}>
        <h3>Packaging Recommendation</h3>
        <div className={styles.recommendation}>
          {packagingOptions.map(opt => (
            <button
              key={opt}
              onClick={() => setPackagingType(opt)}
              className={[
                styles.recommendButton,
                packagingType === opt ? styles.selected : '',
                recommendedPackaging === opt ? styles.recommended : ''
              ].join(' ')}
            >
              {opt} {recommendedPackaging === opt && <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-success)' }}>(Recommended)</span>}
            </button>
          ))}
        </div>
        {surchargeWarning && (
          <div className={styles.surchargeWarning}>
            Warning: This box size may trigger dimensional-weight surcharges from carriers.
          </div>
        )}
        <div className={styles.info}>
          You can compare packaging options above. Recommendation is based on artwork size and fragility.
        </div>
      </div>
      <div style={{ marginBottom: 'var(--spacing-lg)' }}>
        <h3>Shipping Rates</h3>
        {ratesLoading ? (
          <div className={styles.info}>Loading rates...</div>
        ) : ratesError ? null : (
          <div className={styles.rates}>
            {rates.map(rate => (
              <label key={rate.id} className={[
                styles.rateLabel,
                selectedRateId === rate.id ? styles.selected : ''
              ].join(' ')}>
                <input
                  type="radio"
                  name="shippingRate"
                  value={rate.id}
                  checked={selectedRateId === rate.id}
                  onChange={() => setSelectedRateId(rate.id)}
                  style={{ marginRight: 16 }}
                />
                <div style={{ flex: 1 }}>
                  <div className={styles.label}>{rate.carrier} {rate.service}</div>
                  <div className={styles.info}>Est. {rate.est_days} days</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className={styles.ratePrice}>
                    ${rate.price.toFixed(2)}
                  </div>
                  <div className={styles.info}>
                    You're saving ${Math.round(rate.retail - rate.price)} vs. walk-in rates
                  </div>
                </div>
              </label>
            ))}
            {rates.length === 0 && <div className={styles.info}>No rates available for these settings.</div>}
          </div>
        )}
      </div>
      <div className={styles.buttonGroup}>
        <button onClick={() => navigate('/shipping')} className={styles.cancelButton}>Cancel</button>
        <button disabled={!selectedRateId || !checkoutUrl} onClick={handleContinue} className={styles.continueButton}>Continue</button>
      </div>
    </div>
  );
};

export default ShipArtworkPage; 